'use client';
import type { DataGridPaginationState, DataGridSortingState } from '@magicschool/spellbook';
import {
  type InfiniteData,
  keepPreviousData,
  QueryClient,
  QueryClientProvider as QueryClientProviderBase,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { TeacherHistoryFilters } from '@/app/(teacher)/history/_hooks/useTeacherHistoryData';

const client = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 60000, // 1 minute
      staleTime: 60000, // 1 minute
    },
  },
});

function QueryClientProvider(props: { children: React.ReactNode }) {
  return (
    <QueryClientProviderBase client={client}>
      {props.children}
      {!process.env.NEXT_PUBLIC_DISABLE_REACT_QUERY_DEVTOOLS && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
    </QueryClientProviderBase>
  );
}

const queryFactory = {
  customPrompts: ['customPrompts'] as const,
  translationStatus: () => ['translation_status'] as const,
  userFeedback: (userId?: string) => ['user-feedback', userId].filter(Boolean),
  thread: (thread?: string) => ['thread', thread].filter(Boolean),
  threads: (thread?: string) => ['threads', thread].filter(Boolean),
  siteConfig: (userId?: string) => ['site-config', userId].filter(Boolean),
  searchExcerpts: (filters?: unknown) => ['excerpt_search', filters].filter(Boolean),
  resourceById: (id: number) => ['resource', id] as const,
  /**
   * One deck, addressed either by its row id (My Resources) or by the `createPresentation` call
   * that made it (the in-thread card and the panel). One namespace, so a save can update every
   * view of the deck.
   */
  presentationDeck: (lookup: string | { threadId: number; toolCallId: string; shareLinkId: string | null }) =>
    typeof lookup === 'string'
      ? (['presentation_deck', 'id', lookup] as const)
      : (['presentation_deck', 'tool_call', lookup.threadId, lookup.toolCallId, lookup.shareLinkId] as const),
  threadResources: (threadId?: number, userId?: string) => ['thread_resources', threadId, userId].filter(Boolean),
  userResource: (kind: 'excerpt' | 'studio_document' | 'presentation_deck', id: string | number, userId?: string) =>
    ['user_resource', kind, id, userId].filter(Boolean),
  userData: (userId?: string) => ['userData', userId].filter(Boolean),
  useUser: (userId?: string) => ['user', userId].filter(Boolean),
  useOrgDetails: (orgId?: string) => ['org_details', orgId].filter(Boolean),
  usePrevToolInputs: (uid: string, tool_id: string) => ['prev_inputs', uid, tool_id].filter(Boolean),
  userFavoriteByToolID: (uid: string, tool_id: string) => ['user_favorite_by_tool_id', uid, tool_id].filter(Boolean),
  orgUsers: (pagination?: unknown, sorting?: unknown, search?: unknown, columnFilters?: unknown) =>
    ['org-users', pagination, sorting, search, columnFilters].filter(Boolean),
  orgUserProfile: (userId?: string) => ['org-user-profile', userId].filter(Boolean),
  orgInvitations: (orgId?: string, pagination?: unknown, sorting?: unknown, search?: unknown) =>
    ['org-invitations', orgId, pagination, sorting, search].filter(Boolean),
  /** /o/tools tool invites — not `orgInvitations` (admin email invites). */
  orgToolInvitations: (orgId?: string) => ['org-tool-invitations', orgId].filter(Boolean),
  orgRooms: (pagination?: unknown, sorting?: unknown, search?: unknown, status?: unknown) =>
    ['org-rooms', pagination, sorting, search, status].filter(Boolean),
  orgRoomsSummary: () => ['org-rooms-summary'],
  classes: (pagination?: unknown, sorting?: unknown, search?: unknown) => ['classes', pagination, sorting, search].filter(Boolean),
  orgUserClasses: (userId?: string) => ['org-user-classes', userId].filter(Boolean),
  classDetail: (classId?: string) => ['class-detail', classId].filter(Boolean),
  classStudents: (classId?: string, pagination?: unknown, sorting?: unknown, search?: unknown) =>
    ['class-students', classId, pagination, sorting, search].filter(Boolean),
  studentProfile: (classId?: string, studentId?: string) => ['student-profile', classId, studentId].filter(Boolean),
  orgTools: (pagination?: unknown, sorting?: unknown, filter?: unknown, orgId?: string, columnFilters?: unknown) =>
    ['org-tools', pagination, sorting, filter, orgId, columnFilters].filter(Boolean),
  teacherHistory: (
    pagination?: DataGridPaginationState,
    sorting?: DataGridSortingState,
    search?: string,
    filters?: TeacherHistoryFilters,
  ) => ['teacherHistory', pagination, sorting, search, filters].filter(Boolean),
};

export { QueryClientProvider, client, keepPreviousData, queryFactory, useInfiniteQuery, useQuery, useQueryClient, type InfiniteData };
